import React, { FC } from 'react';
import { Platform, View } from 'react-native';
import ColorWrapper from '@styles/colors';
import { isWeb, isAndroid } from '@helpers';
import { scale } from '@components/frame';
import { ColorFunction, WithColorProps } from '@styles/colors/types';

interface ShadowComponentProps extends WithColorProps {
  borderRadius?: number;
  borderRadiusConfig?: any;
  type?: string;
  bg?: string;
}

const ShadowComponent: FC<ShadowComponentProps> = ({ C, borderRadius, borderRadiusConfig, type = 'Field', bg }) => {
  const config = (() => {
    let t = type;
    if (type === 'Field') {
      t = isWeb ? `${t} [web]` : `${t} [mobile]`;
    }

    const filed = [
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: scale(1.4),
        },
        shadowOpacity: 0.13,
        shadowRadius: scale(5.4),
        elevation: 4,
      },
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: scale(0.45),
        },
        shadowOpacity: 0.11,
        shadowRadius: scale(1.35),
        elevation: 4,
      },
    ];

    const wide = [
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: scale(6.4),
        },
        shadowOpacity: 0.13,
        shadowRadius: scale(14.4),
        elevation: 4,
      },
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: scale(1.2),
        },
        shadowOpacity: 0.11,
        shadowRadius: scale(3.6),
        elevation: 4,
      },
    ];

    const symmetric = [
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: 0,
        },
        shadowOpacity: 0.2,
        shadowRadius: scale(3),
        elevation: 4,
      },
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: 0,
        },
        shadowOpacity: 0.03,
        shadowRadius: scale(20),
        elevation: 4,
      },
    ];

    const filedDark = [
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: scale(1.6),
        },
        shadowOpacity: 0.13,
        shadowRadius: scale(3.6),
        elevation: 4,
      },
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: scale(0.3),
        },
        shadowOpacity: 0.11,
        shadowRadius: scale(0.9),
        elevation: 4,
      },
    ];

    const narrowDark = [
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: 0,
        },
        shadowOpacity: 0.2,
        shadowRadius: scale(36),
        elevation: 4,
      },
    ];

    const symmetricDark = [
      {
        shadowColor: 'black',
        shadowOffset: {
          width: 0,
          height: 0,
        },
        shadowOpacity: 0.3,
        shadowRadius: scale(12),
        elevation: 4,
      },
    ];

    const key = [
      {
        shadowColor: '#898A8D',
        shadowOffset: {
          width: 0,
          height: scale(1.15),
        },
        shadowOpacity: 1,
        shadowRadius: 0,
        elevation: 4,
      },
    ];

    const keyDark = [
      {
        shadowColor: '#000000',
        shadowOffset: {
          width: 0,
          height: scale(1.15),
        },
        shadowOpacity: 1,
        shadowRadius: 0,
        elevation: 4,
      },
    ];

    const isLight = C.scheme === 'light';

    switch (t) {
      case 'Narrow':
        return isLight ? filed : narrowDark;
      case 'Field [web]':
        return isLight ? filed : filedDark;
      case 'Wide':
        return isLight ? wide : {};
      case 'Symmetric':
        return isLight ? symmetric : symmetricDark;
      case 'Key':
        return isLight ? key : keyDark;
      default:
        return [];
    }
  })();

  if (isAndroid) {
    return <View />;
  }

  return [
    config[0] && (
      <View
        key="Shadow-1"
        style={[
          {
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: bg,
          },
          borderRadius && {
            borderRadius,
          },
          borderRadiusConfig && {
            ...borderRadiusConfig,
          },
          config[0],
        ]}
      />
    ),
    config[1] && (
      <View
        key="Shadow-2"
        style={[
          {
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: bg,
          },
          borderRadius && {
            borderRadius,
          },
          borderRadiusConfig && {
            ...borderRadiusConfig,
          },
          config[1],
        ]}
      />
    ),
  ];
};

export const Shadow = ColorWrapper(ShadowComponent);

export type ShadowType = 'Narrow' | 'Narrow Strong' | 'Field' | 'Wide' | 'Very Wide' | 'Symmetric' | 'Key' | 'Ultra Wide';

export const getElevation = (type: ShadowType) => {
  const veryWide = 6;
  const wide = 4;
  const narrow = 3;
  const narrowStrong = 2;
  const symmetric = 5;
  const key = 1;

  switch (type) {
    case 'Narrow':
      return narrow;
    case 'Narrow Strong':
      return narrowStrong;
    case 'Wide':
      return wide;
    case 'Very Wide':
      return veryWide;
    case 'Symmetric':
      return symmetric;
    case 'Key':
      return key;
    default:
      return 0;
  }
};

interface BoxShadowStyle {
  boxShadow: string;
  elevation?: number;
}

export const getBoxShadowStyle = ({
  type,
  C,
  scheme,
  onlyForWeb = false,
}: {
  type: ShadowType;
  C?: ColorFunction;
  scheme?: 'light' | 'dark';
  onlyForWeb?: boolean;
}): BoxShadowStyle | undefined => {
  const _scheme = scheme || C?.scheme || 'light';
  const isLight = _scheme === 'light';
  const isAndroid = Platform.OS === 'android';

  const getBaseStyle = (): BoxShadowStyle | undefined => {
    switch (type) {
      case 'Narrow':
        return isLight
          ? {
              boxShadow: '0px 1.4px 5.4px rgba(0, 0, 0, 0.13), 0px 0.45px 1.35px rgba(0, 0, 0, 0.11)',
            }
          : {
              boxShadow: '0px 0px 36px rgba(0, 0, 0, 0.2)',
            };

      case 'Narrow Strong':
        return isLight
          ? {
              boxShadow: '0px 1.4px 5.4px rgba(0, 0, 0, 0.2), 0px 0.45px 1.35px rgba(0, 0, 0, 0.2)',
            }
          : {
              boxShadow: '0px 0px 36px rgba(0, 0, 0, 0.3)',
            };

      case 'Field':
        return isLight
          ? {
              boxShadow: '0px 1.4px 5.4px rgba(0, 0, 0, 0.13), 0px 0.45px 1.35px rgba(0, 0, 0, 0.11)',
            }
          : {
              boxShadow: '0px 1.6px 3.6px rgba(0, 0, 0, 0.13), 0px 0.3px 0.9px rgba(0, 0, 0, 0.11)',
            };

      case 'Wide':
        return isLight
          ? {
              boxShadow: '0px 6.4px 14.4px rgba(0, 0, 0, 0.13), 0px 1.2px 3.6px rgba(0, 0, 0, 0.11)',
            }
          : undefined;

      case 'Very Wide':
        return { boxShadow: '0px 0px 30px rgba(0, 0, 0, 0.07), 0px 6.4px 14.4px rgba(0, 0, 0, 0.15), 0px 1.2px 3.6px rgba(0, 0, 0, 0.14)' };

      case 'Ultra Wide':
        return {
          boxShadow:
            '0px 4px 250px rgba(0, 0, 0, 0.30), 0px 0px 30px rgba(0, 0, 0, 0.05), 0px 6.4px 14.4px rgba(0, 0, 0, 0.04), 0px 1.2px 3.6px rgba(0, 0, 0, 0.04)',
        };

      case 'Symmetric':
        return isLight
          ? {
              boxShadow: '0px 0px 3px rgba(0, 0, 0, 0.2), 0px 0px 20px rgba(0, 0, 0, 0.03)',
            }
          : {
              boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.3)',
            };

      case 'Key':
        return isLight
          ? {
              boxShadow: '0px 1.15px 0px #898A8D',
            }
          : {
              boxShadow: '0px 1.15px 0px #000000',
            };

      default:
        return undefined;
    }
  };

  const baseStyle = getBaseStyle();

  if (!onlyForWeb && isAndroid && baseStyle) {
    const elevation = getElevation(type);
    return {
      ...baseStyle,
      elevation,
    };
  }

  return baseStyle;
};
